import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { CSSProperties, FocusEvent, ForwardedRef, ReactElement, forwardRef, useState } from 'react';
import { Locale } from '@synergy/models';
import { getCurrencySymbol } from '@synergy/utils';
import { styled } from '@mui/material';
import { useIntl } from 'react-intl';

import { PoolTextfield, PoolTextfieldProps } from '../pool-textfield';

interface EventStub {
    target: {
        id: string;
        name: string;
        value?: number;
    };
}

interface NumericTextfieldProps {
    id: string;
    name: string;
    fixedDecimalScale?: boolean;
    decimalScale?: number;
    onChange: (event: EventStub) => void;
    onBlur: (event: EventStub) => void;
    noFractionalNumbers?: boolean;
    noNegativeNumbers?: boolean;
    hasThousandSeparator?: boolean;
    placeholder?: string;
    prefix?: string;
    suffix?: string;
    /**
     * Pads the shown value to at least this many decimals once the field is
     * left: 0 reads "0,00", 12.5 reads "12,50", 89.1234 stays. Only what is
     * shown changes, never the value; an empty field stays empty. Amounts,
     * prices and percentages set 2, quantities leave it out (LST-S20, R-269).
     */
    minDecimalScale?: number;
    onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
}

/** How many decimals a value already has: 12.5 has one, 89.1234 four. */
const decimalsOf = (value: unknown): number => {
    const text = null === value || undefined === value ? '' : String(value);

    return text.includes('.') ? text.split('.')[1].length : 0;
};

function NumericTextfieldRef(
    props: NumericTextfieldProps,
    ref: ForwardedRef<NumberFormat<NumericTextfieldProps>>
): ReactElement {
    const {
        id,
        name,
        onChange,
        onBlur,
        fixedDecimalScale,
        decimalScale,
        noFractionalNumbers,
        noNegativeNumbers,
        hasThousandSeparator = true,
        placeholder,
        minDecimalScale,
        onFocus,
        ...other
    } = props;

    const intl = useIntl();
    const decimalNumber = 1000.1;
    const parts = intl.formatNumberToParts(decimalNumber);
    const thousandSeparator = hasThousandSeparator ? parts.find(part => part.type === 'group')?.value ?? '.' : false;
    const decimalSeparator = parts.find(part => part.type === 'decimal')?.value ?? ',';

    const handleValueChange = (values: NumberFormatValues): void => {
        const value = values.floatValue;
        onChange({ target: { id, name, value } });
    };

    const handleBlur = (): void => {
        setFocused(false);
        onBlur({ target: { id, name } });
    };

    // While someone types, the figure stays as typed; padding it then would
    // move the caret (#2239 R-140).
    const [focused, setFocused] = useState(false);
    const shown = (other as { value?: unknown }).value;
    const padded =
        !focused &&
        !noFractionalNumbers &&
        (minDecimalScale ?? 0) > 0 &&
        '' !== shown &&
        null !== shown &&
        undefined !== shown;

    return (
        <NumberFormat
            {...other}
            name={name}
            id={id}
            getInputRef={ref}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            decimalScale={
                padded ? Math.max(minDecimalScale ?? 0, decimalsOf(shown)) : noFractionalNumbers ? 0 : decimalScale
            }
            fixedDecimalScale={padded || fixedDecimalScale}
            onFocus={(event: FocusEvent<HTMLInputElement>) => {
                setFocused(true);
                onFocus?.(event);
            }}
            allowNegative={!noNegativeNumbers}
            onValueChange={handleValueChange}
            onBlur={handleBlur}
            placeholder={placeholder || '0'}
        />
    );
}

const NumericTextfield = forwardRef(NumericTextfieldRef);

export type PoolNumberTextfieldProps = PoolTextfieldProps & {
    fixedDecimalScale?: boolean;
    decimalScale?: number;
    noFractionalNumbers?: boolean;
    noNegativeNumbers?: boolean;
    hasThousandSeparator?: boolean;
    /**
     * Pads the shown value to at least this many decimals once the field is
     * left: 0 reads "0,00", 12.5 reads "12,50", 89.1234 stays. Only what is
     * shown changes, never the value; an empty field stays empty. Amounts,
     * prices and percentages set 2, quantities leave it out (LST-S20, R-269).
     */
    minDecimalScale?: number;
    prefix?: string;
    suffix?: string;
    currency?: string;
    /**
     * Whether the currency sign stands in the field.
     *
     * In a table it does not: the sign belongs in the column heading, and
     * repeated down fifty rows it only costs the figures room (LST-S17).
     * The figure keeps its formatting either way.
     */
    currencySymbol?: boolean;
    locale?: Locale;
};

// HINT: If used with Formik, the value needs to be "null" for proper validation
export const PoolNumberTextfield = (props: PoolNumberTextfieldProps): ReactElement => {
    const {
        noFractionalNumbers,
        noNegativeNumbers,
        fixedDecimalScale,
        decimalScale,
        suffix,
        prefix,
        hasThousandSeparator,
        minDecimalScale,
        currency,
        currencySymbol = true,
        locale,
        ...textfieldProps
    } = props;

    const inputProps = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        inputComponent: NumericTextfield as any,
        inputProps: {
            noFractionalNumbers,
            noNegativeNumbers,
            fixedDecimalScale,
            decimalScale,
            hasThousandSeparator,
            minDecimalScale,
            suffix:
                !suffix && currencySymbol && currency && locale ? ` ${getCurrencySymbol(currency, locale)}` : suffix,
            prefix,
            currency,
            currencySymbol,
            locale,
            name: props.name,
            ...textfieldProps.inputProps,
        },
    };

    // The caller's InputProps are merged rather than replaced, and the numeric
    // input wins: passing an adornment used to drop inputComponent and with it
    // the whole number formatting (#2215, R-69).
    return <PoolTextfield {...textfieldProps} InputProps={{ ...textfieldProps.InputProps, ...inputProps }} />;
};
